import { Calendar, Clock, Plus, Settings, User, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { TimeSelect } from "@/components/ui/time-select";
import type { WorkerDetailFragment } from "@/gql/graphql";

interface WorkerManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  worker?: WorkerDetailFragment | null;
  onSave: (workerData: Partial<WorkerDetailFragment>) => Promise<void>;
}

const DAYS_OF_WEEK = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const DEFAULT_AVAILABILITY = {
  monday: [{ start: "09:00", end: "17:00" }],
  tuesday: [{ start: "09:00", end: "17:00" }],
  wednesday: [{ start: "09:00", end: "17:00" }],
  thursday: [{ start: "09:00", end: "17:00" }],
  friday: [{ start: "09:00", end: "17:00" }],
  saturday: [],
  sunday: [],
};

const WorkerManagementDialog: React.FC<WorkerManagementDialogProps> = ({
  isOpen,
  onClose,
  worker,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    bio: "",
    profileImageUrl: "",
    color: "#3b82f6",
    isActive: true,
    canAcceptOnlineBookings: true,
    hireDate: "",
    availability: DEFAULT_AVAILABILITY,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const isEditing = !!worker;

  // Update form data when worker changes
  useEffect(() => {
    if (worker) {
      setFormData({
        name: `${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`,
        email: worker.user?.email || "",
        bio: worker.bio || "",
        profileImageUrl: worker.profileImageUrl || "",
        color: worker.color || "#3b82f6",
        isActive: worker.isActive ?? true,
        canAcceptOnlineBookings: worker.canAcceptOnlineBookings ?? true,
        hireDate: "",
        availability: DEFAULT_AVAILABILITY,
      });
    } else {
      // Reset for new worker
      setFormData({
        name: "",
        email: "",
        bio: "",
        profileImageUrl: "",
        color: "#3b82f6",
        isActive: true,
        canAcceptOnlineBookings: true,
        hireDate: "",
        availability: DEFAULT_AVAILABILITY,
      });
    }
  }, [worker]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Calculate total hours for a day
  const calculateDayHours = (timeRanges: Array<{ start: string; end: string }>) => {
    if (!timeRanges || timeRanges.length === 0) return 0;

    return timeRanges.reduce((total, range) => {
      const start = new Date(`2000-01-01T${range.start}:00`);
      const end = new Date(`2000-01-01T${range.end}:00`);
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return total + (hours > 0 ? hours : 0);
    }, 0);
  };

  // Calculate total weekly hours
  const calculateWeeklyHours = () => {
    return DAYS_OF_WEEK.reduce((total, { key }) => {
      const dayRanges = formData.availability[key] || [];
      return total + calculateDayHours(dayRanges);
    }, 0);
  };

  const handleTimeRangeChange = (
    day: string,
    rangeIndex: number,
    field: "start" | "end",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: prev.availability[day].map((range: any, index: number) =>
          index === rangeIndex ? { ...range, [field]: value } : range
        ),
      },
    }));
  };

  const addTimeRange = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: [...prev.availability[day], { start: "09:00", end: "17:00" }],
      },
    }));
  };

  const removeTimeRange = (day: string, rangeIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: prev.availability[day].filter((_: any, index: number) => index !== rangeIndex),
      },
    }));
  };

  const toggleDayAvailability = (day: string) => {
    const dayRanges = formData.availability[day] || [];
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: dayRanges.length > 0 ? [] : [{ start: "09:00", end: "17:00" }],
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Clean availability data by removing __typename fields
      const cleanAvailability = Object.fromEntries(
        Object.entries(formData.availability).map(([day, ranges]) => [
          day,
          Array.isArray(ranges)
            ? ranges.map(({ start, end }) => ({ start, end })) // Remove __typename
            : ranges,
        ])
      );

      const submitData = {
        ...formData,
        availability: cleanAvailability,
        hireDate: formData.hireDate ? new Date(formData.hireDate) : undefined,
      };

      await onSave(submitData);
      onClose();
    } catch (error) {
      console.error("Error saving worker:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {isEditing ? "Edit Worker" : "Add New Worker"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Work Settings
              </TabsTrigger>
              <TabsTrigger value="availability" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Availability
              </TabsTrigger>
            </TabsList>

            <div
              className="overflow-y-auto flex-1 mt-4 pr-2 -mr-2"
              style={{ maxHeight: "calc(90vh - 200px)" }}
            >
              <TabsContent value="basic" className="space-y-6 mt-0">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Personal Details
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hireDate">Hire Date</Label>
                      <Input
                        id="hireDate"
                        type="date"
                        value={formData.hireDate}
                        onChange={(e) => handleInputChange("hireDate", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="color">Worker Color</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="color"
                          type="color"
                          value={formData.color}
                          onChange={(e) => handleInputChange("color", e.target.value)}
                          className="w-16 h-10"
                        />
                        <div
                          className="w-8 h-8 rounded-full border-2 border-gray-300"
                          style={{ backgroundColor: formData.color }}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => handleInputChange("bio", e.target.value)}
                      rows={3}
                      placeholder="Brief description about the worker..."
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="settings" className="space-y-6 mt-0">
                {/* Work Status */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Worker Status
                  </h3>
                  <p className="text-sm text-gray-600">
                    Configure the worker's status and permissions
                  </p>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label htmlFor="isActive" className="text-base font-medium">
                          Active Worker
                        </Label>
                        <p className="text-sm text-gray-500">
                          Worker can be assigned to appointments and appears in schedules
                        </p>
                      </div>
                      <Switch
                        id="isActive"
                        checked={formData.isActive}
                        onCheckedChange={(checked) => handleInputChange("isActive", checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label htmlFor="canAcceptOnlineBookings" className="text-base font-medium">
                          Online Bookings
                        </Label>
                        <p className="text-sm text-gray-500">
                          Accept appointments from online booking system and public calendar
                        </p>
                      </div>
                      <Switch
                        id="canAcceptOnlineBookings"
                        checked={formData.canAcceptOnlineBookings}
                        onCheckedChange={(checked) =>
                          handleInputChange("canAcceptOnlineBookings", checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Capacity & Limits */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Capacity & Limits</h3>
                  <p className="text-sm text-gray-600">
                    Configure worker settings and availability
                  </p>
                </div>

                {/* Settings Summary */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Current Configuration:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• Status: {formData.isActive ? "Active" : "Inactive"}</p>
                    <p>
                      • Online Bookings: {formData.canAcceptOnlineBookings ? "Enabled" : "Disabled"}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="availability" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Header with total hours */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Weekly Schedule
                      </h3>
                      <p className="text-sm text-gray-600">Set multiple time ranges for each day</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {calculateWeeklyHours().toFixed(1)}h
                      </div>
                      <div className="text-sm text-gray-500">Total weekly hours</div>
                    </div>
                  </div>

                  {/* Days Schedule */}
                  <div className="space-y-4">
                    {DAYS_OF_WEEK.map(({ key, label }) => {
                      const dayRanges = formData.availability[key] || [];
                      const dayHours = calculateDayHours(dayRanges);

                      return (
                        <div key={key} className="p-4 border rounded-lg">
                          {/* Day Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={dayRanges.length > 0}
                                onCheckedChange={() => toggleDayAvailability(key)}
                              />
                              <div>
                                <Label className="text-base font-medium">{label}</Label>
                                {dayRanges.length > 0 && (
                                  <p className="text-sm text-gray-500">
                                    {dayHours.toFixed(1)} hours • {dayRanges.length} time range
                                    {dayRanges.length !== 1 ? "s" : ""}
                                  </p>
                                )}
                              </div>
                            </div>

                            {dayRanges.length === 0 ? (
                              <span className="text-sm text-gray-500">Not working</span>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => addTimeRange(key)}
                                className="flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                Add Range
                              </Button>
                            )}
                          </div>

                          {/* Time Ranges */}
                          {dayRanges.length > 0 && (
                            <div className="space-y-3 ml-11">
                              {dayRanges.map((range: any, rangeIndex: number) => (
                                <div
                                  key={rangeIndex}
                                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                                >
                                  <div className="flex items-center gap-2">
                                    <Label className="text-sm text-gray-600">From:</Label>
                                    <TimeSelect
                                      value={range.start}
                                      onChange={(value) =>
                                        handleTimeRangeChange(key, rangeIndex, "start", value)
                                      }
                                    />
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Label className="text-sm text-gray-600">To:</Label>
                                    <TimeSelect
                                      value={range.end}
                                      onChange={(value) =>
                                        handleTimeRangeChange(key, rangeIndex, "end", value)
                                      }
                                    />
                                  </div>

                                  <div className="text-sm text-gray-600 w-16 text-center">
                                    {(() => {
                                      const start = new Date(`2000-01-01T${range.start}:00`);
                                      const end = new Date(`2000-01-01T${range.end}:00`);
                                      const hours =
                                        (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                                      return hours > 0 ? `${hours.toFixed(1)}h` : "0h";
                                    })()}
                                  </div>

                                  {dayRanges.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeTimeRange(key, rangeIndex)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}

                              {/* Add more time range button */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => addTimeRange(key)}
                                className="w-full border-2 border-dashed border-gray-300 hover:border-gray-400 py-2"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add another time range
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-3">Quick Presets:</h4>
                      <div className="space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => {
                            const weekdaySchedule = [{ start: "09:00", end: "17:00" }];
                            setFormData((prev) => ({
                              ...prev,
                              availability: {
                                monday: weekdaySchedule,
                                tuesday: weekdaySchedule,
                                wednesday: weekdaySchedule,
                                thursday: weekdaySchedule,
                                friday: weekdaySchedule,
                                saturday: [],
                                sunday: [],
                              },
                            }));
                          }}
                        >
                          Weekdays 09:00-17:00 (40h)
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => {
                            const splitSchedule = [
                              { start: "09:00", end: "13:00" },
                              { start: "14:00", end: "18:00" },
                            ];
                            setFormData((prev) => ({
                              ...prev,
                              availability: {
                                monday: splitSchedule,
                                tuesday: splitSchedule,
                                wednesday: splitSchedule,
                                thursday: splitSchedule,
                                friday: splitSchedule,
                                saturday: [],
                                sunday: [],
                              },
                            }));
                          }}
                        >
                          Split Shift 09:00-13:00, 14:00-18:00 (40h)
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              availability: DEFAULT_AVAILABILITY,
                            }));
                          }}
                        >
                          Reset to Default
                        </Button>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">Schedule Summary:</h4>
                      <div className="space-y-1 text-sm">
                        {DAYS_OF_WEEK.map(({ key, label }) => {
                          const dayRanges = formData.availability[key] || [];
                          const dayHours = calculateDayHours(dayRanges);

                          return (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-600">{label}:</span>
                              <span className="font-medium">
                                {dayRanges.length === 0 ? "Off" : `${dayHours.toFixed(1)}h`}
                              </span>
                            </div>
                          );
                        })}
                        <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between font-semibold">
                          <span>Total:</span>
                          <span className="text-blue-600">
                            {calculateWeeklyHours().toFixed(1)}h
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : isEditing ? "Update Worker" : "Create Worker"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkerManagementDialog;
